import * as Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { useTheme } from 'src/state/providers/Theme'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

export default function LecturerCourseScoreStatistics(): JSX.Element | null {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('subPage:score_statistics')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const headers = useAuthenticatedHeaders()
  const [searchParams] = useSearchParams()
  const { courseId } = useParams()
  const activeSemester = useActiveSemester()
  const semId = searchParams.get('semId') ?? activeSemester?.id
  const t = useTranslatable()
  const theme = useTheme()
  const isDarkMode = theme === 'dark'

  const { data } = useApi({
    endpoint: Api.getLecturerCoursesScoresStatistics,
    params: React.useMemo(
      () => ({
        headers,
        args: {
          semId: semId!,
          id: courseId!,
        },
      }),
      [courseId, headers, semId]
    ),
    shouldFetch: semId != null,
  })

  const options: Highcharts.Options = {
    chart: {
      plotBackgroundColor: undefined,
      plotBorderWidth: undefined,
      plotShadow: false,
      type: 'bar',
      ignoreHiddenSeries: true,
      backgroundColor: 'transparent',
      spacingBottom: 20,
      height: 800,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: t('lecturerSurveyResults:students_scores_by_groups'),
      style: {
        color: isDarkMode ? '#ffffff' : '#000000',
      },
    },
    xAxis: {
      type: 'category',
      title: undefined,
      categories: [...(data?.categories.attributes ?? [])],
      ...(isDarkMode && {
        labels: {
          style: {
            color: '#cccccc',
          },
        },
        gridLineColor: '#444444',
      }),
    },
    yAxis: {
      min: 0,
      allowDecimals: false,
      title: {
        text: t('lecturerSurveyResults:students_count'),
        style: {
          color: isDarkMode ? '#ffffff' : '#000000',
        },
        offset: 100,
      },
      ...(isDarkMode && {
        labels: {
          style: {
            color: '#cccccc',
          },
        },
        gridLineColor: '#444444',
      }),
    },
    series: data
      ? data?.groups.attributes.map((group) => ({
          type: 'bar',
          name: group.name,
          data: Array.from(group.data),
          visible: group.visible ?? true,
        }))
      : [],
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y}</b><br/>',
    },
    legend: {
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      enabled: true,
      ...(isDarkMode && {
        itemStyle: {
          color: isDarkMode ? '#ffffff' : '#000000',
        },
        itemHoverStyle: {
          color: '#ffffff',
        },
        itemHiddenStyle: {
          color: '#cccccc',
        },
      }),
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
            plotOptions: {
              pie: {
                size: '70%',
                dataLabels: {
                  enabled: false,
                },
              },
            },
          },
        },
      ],
    },
  }

  return (
    <div className="lecturer-high-chart-wrapper flex min-h-[400px] w-full flex-auto items-center justify-center p-5">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  )
}
